import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, ShoppingBag, Edit, Trash2, BarChart3, List as ListIcon, TrendingUp, Package, DollarSign } from 'lucide-react';
import Swal from 'sweetalert2';
import { toast } from 'sonner';

import API from '../api_config';

const formatCLP = (amount) => new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  minimumFractionDigits: 0
}).format(amount);

function SupplierDashboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/inventory-by-supplier`);
        setData(res.data);
      } catch (error) {
        console.error(error);
        toast.error('Error al cargar métricas de proveedores');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="p-4 text-center">Cargando métricas...</div>;

  const totalValue = data.reduce((sum, item) => sum + item.total_value, 0);

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-950/20 dark:to-background border-indigo-100 dark:border-indigo-900">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Valor Total Inventario</CardTitle>
                <DollarSign className="h-4 w-4 text-indigo-600" />
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-bold font-mono text-indigo-700 dark:text-indigo-400">{formatCLP(totalValue)}</div>
            </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
            <CardTitle>Inventario por Proveedor</CardTitle>
        </CardHeader>
        <CardContent>
            {data.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No hay datos de inventario asociados a proveedores.</p>
            ) : (
                <div className="relative overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-muted-foreground uppercase bg-muted/50">
                            <tr>
                                <th className="px-4 py-3 rounded-l-lg">Proveedor</th>
                                <th className="px-4 py-3 text-center">Ítems Diferentes</th>
                                <th className="px-4 py-3 text-right">Stock Total (Unidades)</th>
                                <th className="px-4 py-3 text-right rounded-r-lg">Valorización (Costo)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item, idx) => (
                                <tr key={idx} className="border-b last:border-0 hover:bg-muted/50 transition-colors">
                                    <td className="px-4 py-3 font-medium">
                                        <div className="flex items-center">
                                            <div className="h-8 w-8 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center mr-3 text-indigo-700 dark:text-indigo-300 font-bold text-xs">
                                                {item.supplier_name.substring(0, 2).toUpperCase()}
                                            </div>
                                            {item.supplier_name}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-center">{item.total_items}</td>
                                    <td className="px-4 py-3 text-right font-mono">{item.total_stock}</td>
                                    <td className="px-4 py-3 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                                        {formatCLP(item.total_value)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function SuppliersPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState('list'); // 'list' | 'dashboard'
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    rut: '',
    address: ''
  });

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Error al cargar proveedores');
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/suppliers/${editingId}`, formData);
        toast.success('Proveedor actualizado');
      } else {
        await axios.post(`${API}/suppliers`, formData);
        toast.success('Proveedor creado');
      }
      setOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error('Error al guardar proveedor');
    }
  };

  const handleEdit = (supplier) => {
    setFormData({
      name: supplier.name,
      email: supplier.email || '',
      phone: supplier.phone || '',
      rut: supplier.rut || '',
      address: supplier.address || ''
    });
    setEditingId(supplier.id);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: "No podrás revertir esta acción",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API}/suppliers/${id}`);
        Swal.fire(
          '¡Eliminado!',
          'El proveedor ha sido eliminado.',
          'success'
        );
        fetchSuppliers();
      } catch (error) {
        Swal.fire(
          'Error',
          'No se pudo eliminar el proveedor.',
          'error'
        );
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', rut: '', address: '' });
    setEditingId(null);
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Proveedores</h1>
          <p className="text-muted-foreground mt-2">Gestiona tus proveedores y analiza tu inventario</p>
        </div>
        
        <div className="flex items-center gap-2 bg-muted p-1 rounded-lg">
            <Button 
                variant={activeTab === 'list' ? 'default' : 'ghost'} 
                size="sm" 
                onClick={() => setActiveTab('list')}
                className="rounded-md"
            >
                <ListIcon className="h-4 w-4 mr-2" />
                Listado
            </Button>
            <Button 
                variant={activeTab === 'dashboard' ? 'default' : 'ghost'} 
                size="sm" 
                onClick={() => setActiveTab('dashboard')}
                className="rounded-md"
            >
                <BarChart3 className="h-4 w-4 mr-2" />
                Dashboard
            </Button>
        </div>

        {activeTab === 'list' && (
            <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val) resetForm(); }}>
            <DialogTrigger asChild>
                <Button className="rounded-full shadow-lg" data-testid="add-supplier-btn">
                <Plus className="h-5 w-5 mr-2" />
                Nuevo Proveedor
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                <DialogTitle>{editingId ? 'Editar Proveedor' : 'Nuevo Proveedor'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                    <Label>Nombre *</Label>
                    <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="supplier-name-input"
                    />
                </div>
                <div className="space-y-2">
                    <Label>RUT</Label>
                    <Input
                    value={formData.rut}
                    onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                    placeholder="12.345.678-9"
                    data-testid="supplier-rut-input"
                    />
                </div>
                <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="supplier-email-input"
                    />
                </div>
                <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="supplier-phone-input"
                    />
                </div>
                <div className="space-y-2">
                    <Label>Dirección</Label>
                    <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    data-testid="supplier-address-input"
                    />
                </div>
                <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
                    Cancelar
                    </Button>
                    <Button type="submit" data-testid="save-supplier-btn">
                    {editingId ? 'Actualizar' : 'Crear'}
                    </Button>
                </div>
                </form>
            </DialogContent>
            </Dialog>
        )}
      </div>

      {activeTab === 'list' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {suppliers.length === 0 ? (
            <Card className="col-span-full">
                <CardContent className="text-center py-12 text-muted-foreground">
                <ShoppingBag className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No hay proveedores registrados</p>
                </CardContent>
            </Card>
            ) : (
            suppliers.map(supplier => (
                <Card key={supplier.id} className="hover:shadow-md transition-shadow relative group" data-testid={`supplier-${supplier.id}`}>
                <div className="absolute top-4 right-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(supplier)}>
                    <Edit className="h-4 w-4 text-blue-600" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(supplier.id)}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                </div>
                <CardContent className="p-6 space-y-2">
                    <h3 className="font-semibold text-lg">{supplier.name}</h3>
                    {supplier.rut && (
                    <p className="text-sm text-muted-foreground font-mono">RUT: {supplier.rut}</p>
                    )}
                    {supplier.email && (
                    <p className="text-sm text-muted-foreground">{supplier.email}</p>
                    )}
                    {supplier.phone && (
                    <p className="text-sm text-muted-foreground">{supplier.phone}</p>
                    )}
                    {supplier.address && (
                    <p className="text-sm text-muted-foreground">{supplier.address}</p>
                    )}
                </CardContent>
                </Card>
            ))
            )}
        </div>
      ) : (
          <SupplierDashboard />
      )}
    </div>
  );
}